import React, { useEffect, useRef, useState } from 'react';
import { ReactComponent as ReplayIcon } from 'img/icons/reset.svg';

import { Box, makeStyles, Theme } from '@material-ui/core';
import { TextFieldProps } from '@material-ui/core/TextField/TextField';
import { isBulkConstant } from 'common/constants/bulkConstants';
import { TextField } from 'common/components/index';
import cx from 'classnames';

const useStyles = makeStyles((theme: Theme) => ({
  mainContainer: {
    position: 'relative',
  },
  replayContainer: {
    backgroundColor: theme.palette.primary.main,
    position: 'absolute',
    zIndex: 1,
    right: 0,
    borderRadius: 3,
    cursor: 'pointer',
    height: 18,
    width: 20,
    textAlign: 'center',
  },
  iconReplay: {
    fill: theme.palette.background.paper,
    height: 16,
    width: 16,
    padding: '2px 2px 0px',
  },
  input: {
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: theme.palette.primary.main,
    },
    '& :hover .MuiOutlinedInput-notchedOutline': {
      borderColor: theme.palette.primary.main,
    },
  },
}));

type TextFieldWithResetProps = Omit<TextFieldProps, 'onChange'> & {
  reset?: string | number;
  multi?: boolean;
  inputComponent?: JSX.Element;
  isDefaultCanResetFlag?: boolean;
  onChange?: (val: string | number) => void;
  recommended?: boolean;
};

const TextFieldWithReset = ({
  InputProps,
  multi,
  reset,
  isDefaultCanResetFlag,
  recommended,
  ...textFieldProps
}: TextFieldWithResetProps): JSX.Element => {
  const classes = useStyles();
  const { name, value, onChange, error, variant } = textFieldProps;
  const [canReset, setCanReset] = useState(false);
  const refInput = useRef<HTMLElement | null>(null);

  const handleResetBtnClick = () => {
    setCanReset(false);

    if (onChange && reset !== undefined) {
      onChange(reset);
    }
  };

  const handleInputFocus = () => {
    if (multi && !canReset && reset !== undefined) {
      setCanReset(true);

      if (onChange && isBulkConstant(value)) {
        onChange('');
        setTimeout(() => refInput.current?.focus());
      }
    }
  };

  useEffect(() => {
    if (isDefaultCanResetFlag && canReset) {
      setCanReset(false);
    }
  }, [isDefaultCanResetFlag, setCanReset, canReset]);

  const shouldShowResetButton = canReset && reset !== value;
  const multipleValsInputProps =
    multi && isBulkConstant(value)
      ? {
          ...InputProps,
          inputComponent: undefined,
          inputProps: undefined,
        }
      : InputProps;

  return (
    <Box className={classes.mainContainer}>
      {shouldShowResetButton && (
        <Box className={classes.replayContainer} onClick={handleResetBtnClick}>
          <ReplayIcon className={classes.iconReplay} />
        </Box>
      )}

      <TextField
        name={name}
        inputRef={refInput}
        {...textFieldProps}
        variant={variant}
        onChange={event => {
          if (onChange) {
            if (typeof event === 'string' || typeof event === 'number') {
              onChange(event);
            } else {
              onChange(event.target?.value || '');
            }
          }
        }}
        InputProps={multipleValsInputProps}
        value={multi && isBulkConstant(value) ? 'Multiple' : value}
        onFocus={handleInputFocus}
        className={cx(
          { [classes.input]: shouldShowResetButton && !error },
          textFieldProps.className,
        )}
        recommended={recommended}
      />
    </Box>
  );
};

export default TextFieldWithReset;
